import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const SiteAuthContext = createContext();

export const useSiteAuth = () => {
  const context = useContext(SiteAuthContext);
  if (!context) {
    throw new Error('useSiteAuth must be used within SiteAuthProvider');
  }
  return context;
};

export const SiteAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    const token = localStorage.getItem('siteToken');
    
    if (!token) {
      setIsChecking(false);
      return;
    }

    try {
      const response = await axios.get('/api/site-auth/check', {
        headers: {
          'x-site-token': token
        }
      });

      if (response.data.authenticated) {
        setIsAuthenticated(true);
        axios.defaults.headers.common['x-site-token'] = token;
      } else {
        localStorage.removeItem('siteToken');
        delete axios.defaults.headers.common['x-site-token'];
      }
    } catch (error) {
      localStorage.removeItem('siteToken');
      delete axios.defaults.headers.common['x-site-token'];
    } finally {
      setIsChecking(false);
    }
  };

  const login = async (password) => {
    setError('');
    
    try {
      const response = await axios.post('/api/site-auth/verify', {
        password
      });

      if (response.data.success) {
        const { token } = response.data;
        localStorage.setItem('siteToken', token);
        axios.defaults.headers.common['x-site-token'] = token;
        setIsAuthenticated(true);
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Authentication failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('siteToken');
    delete axios.defaults.headers.common['x-site-token'];
    setIsAuthenticated(false);
  };

  return (
    <SiteAuthContext.Provider value={{
      isAuthenticated,
      isChecking,
      error,
      login,
      logout
    }}>
      {children}
    </SiteAuthContext.Provider>
  );
};